import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowLeft,
  FiLoader,
  FiLock,
  FiEye,
  FiCalendar,
  FiClock,
  FiTarget,
  FiFolder,
  FiClipboard,
  FiSend,
  FiUser,
  FiAlertCircle,
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';

import { API, getAvatarUrl } from '../../services/api';

const BRIEF_CATEGORY_LABELS = {
  copywriting: 'Copywriting',
  design: 'Diseño',
  video: 'Vídeo',
  photography: 'Fotografía',
  strategy: 'Estrategia',
  'web-positioning': 'Posicionamiento Web',
  'social-media': 'Redes Sociales',
  animation: 'Animación',
  'web-development': 'Desarrollo Web',
};

const EXPERIENCE_LABELS = {
  any: 'Cualquier nivel',
  junior: 'Junior',
  mid: 'Intermedio',
  senior: 'Senior',
  expert: 'Experto',
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatBudget = (min, max) => {
  if (min != null && max != null) return `${min}€ - ${max}€`;
  if (min != null) return `Desde ${min}€`;
  if (max != null) return `Hasta ${max}€`;
  return 'A negociar';
};

const LockedSection = ({ title, teaser, icon: Icon = FiLock }) => (
  <section className="brief-detail-section brief-detail-section--locked">
    <div className="brief-detail-section-header">
      <h3><Icon aria-hidden="true" /> {title}</h3>
      <span className="brief-detail-locked-pill">
        <FiLock aria-hidden="true" /> Requiere iniciar sesión
      </span>
    </div>
    {teaser && <p className="brief-detail-locked-teaser">{teaser}</p>}
  </section>
);

/**
 * BriefDetailPage
 *
 * Página de detalle de un brief/proyecto.
 * - Si `user` y `token` están presentes: muestra toda la información (endpoint privado).
 * - Si no hay sesión: muestra un teaser con algunos campos + gate con CTA de login.
 *
 * Props:
 *   briefId        – id del brief
 *   user           – usuario autenticado (o null si público)
 *   token          – JWT (o null si público)
 *   onNavigateAuth – callback para ir a la pantalla de login (solo público)
 */
const BriefDetailPage = ({ briefId, user, token, onNavigateAuth }) => {
  const navigate = useNavigate();
  const isAuthed = Boolean(user && token);

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const endpoint = isAuthed
      ? `${API}/briefs/${briefId}`
      : `${API}/public/briefs/${briefId}`;
    const config = isAuthed
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    axios
      .get(endpoint, config)
      .then((res) => {
        if (!cancelled) setBrief(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setError('notfound');
        else if (status === 403) setError('forbidden');
        else setError('generic');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [briefId, isAuthed, token]);

  const goBack = () => {
    if (isAuthed) navigate('/briefs');
    else if (onNavigateAuth) onNavigateAuth();
    else navigate('/');
  };

  const handleApply = () => {
    if (!isAuthed) {
      if (onNavigateAuth) onNavigateAuth();
      else navigate('/');
      return;
    }
    navigate(`/briefs?apply=${briefId}`);
  };

  if (loading) {
    return (
      <div className="brief-detail-page">
        <div className="brief-detail-loading">
          <FiLoader className="loading-spinner" aria-hidden="true" />
          <span>Cargando proyecto…</span>
        </div>
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="brief-detail-page">
        <div className="brief-detail-error">
          <FiAlertCircle aria-hidden="true" />
          <h2>
            {error === 'notfound'
              ? 'Este proyecto no existe o ya no está disponible'
              : 'No hemos podido cargar este proyecto'}
          </h2>
          <button type="button" onClick={goBack} className="btn-secondary">
            <FiArrowLeft aria-hidden="true" /> Volver
          </button>
        </div>
      </div>
    );
  }

  const categoryLabel = BRIEF_CATEGORY_LABELS[brief.category] || brief.category;
  const experienceLabel = EXPERIENCE_LABELS[brief.experience_level] || brief.experience_level;
  const status = brief.status || 'open';
  const isOwnBrief = isAuthed && brief.creator_id === user.id;
  const canApply = isAuthed && user.role === 'creative' && status === 'open';
  const isCreatorViewing = isAuthed && (user.role === 'creator' || user.role === 'admin') && isOwnBrief;

  return (
    <motion.div
      className="brief-detail-page"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button type="button" className="brief-detail-back" onClick={goBack}>
        <FiArrowLeft aria-hidden="true" />
        {isAuthed ? 'Volver a proyectos' : 'Volver'}
      </button>

      <header className="brief-detail-header">
        <div className="brief-detail-header-top">
          <span className={`status-badge ${status}`}>
            {status === 'open' ? 'Abierto' : status === 'in_progress' ? 'En progreso' : 'Completado'}
          </span>
          {brief.category && (
            <span className="tag category-tag">{categoryLabel}</span>
          )}
          {brief.experience_level && (
            <span className="tag experience-tag">{experienceLabel}</span>
          )}
          {brief.project_duration && (
            <span className="tag duration-tag">{brief.project_duration}</span>
          )}
        </div>
        <h1 className="brief-detail-title">{brief.title}</h1>
        <div className="brief-detail-header-row">
          <div className="brief-detail-creator">
            <img
              className="brief-detail-creator-avatar"
              src={getAvatarUrl(brief.creator_avatar, brief.creator_name)}
              alt={brief.creator_name ? `Foto de perfil de ${brief.creator_name}` : 'Foto de perfil'}
            />
            <div className="brief-detail-creator-info">
              <span className="brief-detail-creator-label">Publicado por</span>
              <span className="brief-detail-creator-name">{brief.creator_name || 'Empresa'}</span>
            </div>
          </div>
          {canApply && (
            <button type="button" className="btn-primary brief-detail-apply-btn brief-detail-apply-btn--header" onClick={handleApply}>
              <FiSend aria-hidden="true" />
              Aplicar a este proyecto
            </button>
          )}
          {isCreatorViewing && (
            <button
              type="button"
              className="btn-primary brief-detail-apply-btn brief-detail-apply-btn--header"
              onClick={() => navigate(`/briefs/${brief.id}`)}
            >
              <FiEye aria-hidden="true" />
              Ver aplicaciones
            </button>
          )}
        </div>
      </header>

      <div className="brief-detail-grid">
        <div className="brief-detail-main">
          {/* Descripción */}
          <section className="brief-detail-section">
            <h3><FiEye aria-hidden="true" /> Descripción del proyecto</h3>
            <p className="brief-detail-body">
              {isAuthed ? brief.description : brief.description_preview}
              {!isAuthed && brief.has_more_description && (
                <span className="brief-detail-fade-tail">…</span>
              )}
            </p>
            {!isAuthed && brief.has_more_description && (
              <div className="brief-detail-inline-gate">
                <FiLock aria-hidden="true" />
                <span>Inicia sesión para leer la descripción completa</span>
              </div>
            )}
          </section>

          {/* Público objetivo */}
          {isAuthed ? (
            brief.target_audience && (
              <section className="brief-detail-section">
                <h3><FiUser aria-hidden="true" /> Público objetivo</h3>
                <p className="brief-detail-body">{brief.target_audience}</p>
              </section>
            )
          ) : (
            brief.has_target_audience && (
              <LockedSection
                title="Público objetivo"
                teaser="El proyecto define un público objetivo. Inicia sesión para verlo."
                icon={FiUser}
              />
            )
          )}

          {/* Referencias */}
          {isAuthed ? (
            brief.references && (
              <section className="brief-detail-section">
                <h3><FiEye aria-hidden="true" /> Referencias</h3>
                <p className="brief-detail-body">{brief.references}</p>
              </section>
            )
          ) : (
            brief.has_references && (
              <LockedSection
                title="Referencias"
                teaser="Hay referencias visuales y enlaces disponibles. Inicia sesión para verlos."
                icon={FiEye}
              />
            )
          )}

          {/* Requisitos */}
          {isAuthed ? (
            brief.requirements && brief.requirements.length > 0 && (
              <section className="brief-detail-section">
                <h3><FiClipboard aria-hidden="true" /> Requisitos</h3>
                <ul className="brief-detail-requirements">
                  {brief.requirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </section>
            )
          ) : (
            brief.requirements_count > 0 && (
              <LockedSection
                title="Requisitos"
                teaser={`Este proyecto tiene ${brief.requirements_count} ${brief.requirements_count === 1 ? 'requisito específico' : 'requisitos específicos'}. Inicia sesión para verlos.`}
                icon={FiClipboard}
              />
            )
          )}
        </div>

        <aside className="brief-detail-aside">
          <div className="brief-detail-meta-grid">
            <div className="brief-detail-meta-item">
              <span className="brief-detail-meta-label">
                <FaEuroSign aria-hidden="true" /> Presupuesto
              </span>
              <span className="brief-detail-meta-value">
                {formatBudget(brief.budget_min, brief.budget_max)}
              </span>
            </div>
            <div className="brief-detail-meta-item">
              <span className="brief-detail-meta-label">
                <FiCalendar aria-hidden="true" /> Publicado
              </span>
              <span className="brief-detail-meta-value">{formatDate(brief.created_at)}</span>
            </div>
            {brief.deadline && (
              <div className="brief-detail-meta-item">
                <span className="brief-detail-meta-label">
                  <FiClock aria-hidden="true" /> Fecha límite
                </span>
                <span className="brief-detail-meta-value">{formatDate(brief.deadline)}</span>
              </div>
            )}
            {brief.project_duration && (
              <div className="brief-detail-meta-item">
                <span className="brief-detail-meta-label">
                  <FiClock aria-hidden="true" /> Duración estimada
                </span>
                <span className="brief-detail-meta-value">{brief.project_duration}</span>
              </div>
            )}
            <div className="brief-detail-meta-item">
              <span className="brief-detail-meta-label">
                <FiTarget aria-hidden="true" /> Experiencia requerida
              </span>
              <span className="brief-detail-meta-value">{experienceLabel}</span>
            </div>
            {brief.subcategory && (
              <div className="brief-detail-meta-item">
                <span className="brief-detail-meta-label">
                  <FiFolder aria-hidden="true" /> Subcategoría
                </span>
                <span className="brief-detail-meta-value">{brief.subcategory}</span>
              </div>
            )}
          </div>

          {/* Gate público solo aparece si no hay sesión */}
          {!isAuthed && (
            <div className="brief-detail-public-gate">
              <h3>¿Te interesa este proyecto?</h3>
              <p>
                Crea una cuenta gratis en Vissory para ver todos los detalles,
                contactar con la marca y aplicar si eres creativo.
              </p>
              <div className="brief-detail-public-gate-actions">
                <button type="button" className="btn-primary" onClick={handleApply}>
                  Crear cuenta gratis
                </button>
                <button type="button" className="btn-secondary" onClick={handleApply}>
                  Iniciar sesión
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </motion.div>
  );
};

export default BriefDetailPage;
